import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { communicationsApi } from '../api/communications';
import { documentsApi } from '../api/documents';
import LetterWizardModal from './LetterWizardModal';
import CommunicationEntryModal from './CommunicationEntryModal';
import DocumentViewer from './DocumentViewer';
import type { Communication, CommunicationKind, DocumentItem } from '../types';

interface KommunikationSectionProps {
  entityType: string;
  entityId: number;
  vermieterId?: number | null;
}

const KIND_LABELS: Record<CommunicationKind, string> = {
  call: 'Anruf',
  email: 'E-Mail',
  letter: 'Brief',
};

const KIND_BADGES: Record<CommunicationKind, string> = {
  call: 'bg-emerald-100 text-emerald-800',
  email: 'bg-blue-100 text-blue-800',
  letter: 'bg-indigo-100 text-indigo-800',
};

export const STATUS_BADGES: Record<string, { label: string; class: string }> = {
  draft:    { label: 'Entwurf',   class: 'bg-yellow-100 text-yellow-800' },
  sent:     { label: 'Versendet', class: 'bg-green-100 text-green-800' },
  received: { label: 'Empfangen', class: 'bg-blue-100 text-blue-800' },
};

const DIRECTION_PILLS: Record<string, { label: string; class: string }> = {
  incoming: { label: 'Eingehend',  class: 'bg-slate-100 text-slate-700' },
  outgoing: { label: 'Ausgehend',  class: 'bg-slate-100 text-slate-700' },
};

/** Build a minimal DocumentItem suitable for DocumentViewer from a Communication. */
function syntheticDocFromComm(comm: Communication): DocumentItem | null {
  if (!comm.document_id || !comm.document_filename) return null;
  return {
    id: comm.document_id,
    entity_type: comm.entity_type,
    entity_id: comm.entity_id,
    listing_id: null,
    city_id: comm.city_id,
    document_type: 'template_generated',
    template_name: comm.template_name,
    filename: comm.document_filename,
    mime_type: comm.document_mime_type ?? 'application/octet-stream',
    file_size: null,
    version: 1,
    notes: null,
    created_at: comm.occurred_at,
    updated_at: null,
  };
}

export default function KommunikationSection({
  entityType,
  entityId,
}: KommunikationSectionProps) {
  const queryClient = useQueryClient();
  const [letterWizardOpen, setLetterWizardOpen] = useState(false);
  const [letterEditing, setLetterEditing] = useState<Communication | null>(null);
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [entryEditing, setEntryEditing] = useState<Communication | null>(null);
  const [viewingDoc, setViewingDoc] = useState<DocumentItem | null>(null);

  const cacheKey = ['communications', entityType, entityId];

  const { data: communications = [], isLoading } = useQuery({
    queryKey: cacheKey,
    queryFn: () => communicationsApi.getCommunications(entityType, entityId),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => communicationsApi.deleteCommunication(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cacheKey }),
  });

  const finalizeMut = useMutation({
    mutationFn: (id: number) => communicationsApi.finalizeLetter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cacheKey });
      queryClient.invalidateQueries({ queryKey: ['documents', entityType, entityId] });
    },
  });

  const editCommunication = (comm: Communication) => {
    if (comm.kind === 'letter' && comm.status === 'draft' && comm.template_name) {
      setLetterEditing(comm);
    } else {
      setEntryEditing(comm);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Section header */}
      <div className="px-6 py-4 border-b flex items-center justify-between gap-3">
        <h3 className="text-lg font-medium">
          Kommunikation
          <span className="ml-2 text-sm font-normal text-gray-500">({communications.length})</span>
        </h3>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLetterWizardOpen(true)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            + Brief aus Vorlage
          </button>
          <button
            onClick={() => setEntryModalOpen(true)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            + Eintrag
          </button>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="px-6 py-6 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      ) : communications.length === 0 ? (
        <p className="px-6 py-4 text-sm text-gray-500">Noch keine Kommunikation erfasst.</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {communications.map((comm) => {
            const isLetter = comm.kind === 'letter';
            const isEmail = comm.kind === 'email';
            const isCall = comm.kind === 'call';
            const isDraft = comm.status === 'draft';
            const isSent = comm.status === 'sent';
            const isOutgoing = comm.direction === 'outgoing';
            const statusBadge = !isCall && comm.status ? STATUS_BADGES[comm.status] : null;
            const directionPill = isCall ? DIRECTION_PILLS[comm.direction] : null;
            const isPdfDoc = comm.document_mime_type === 'application/pdf';
            const isOdtDoc = comm.document_mime_type === 'application/vnd.oasis.opendocument.text';
            // Smart row click: continue editing draft / open attached doc / fall back to entry modal.
            const handleRowClick = () => {
              if (isLetter && isDraft && comm.template_name) {
                setLetterEditing(comm);
              } else if (comm.document_id && comm.document_filename) {
                const doc = syntheticDocFromComm(comm);
                if (doc) {
                  const mime = doc.mime_type ?? '';
                  if (mime === 'application/pdf' || mime.startsWith('image/')) {
                    setViewingDoc(doc);
                  } else {
                    documentsApi.downloadDocument(doc.id, doc.filename);
                  }
                }
              } else {
                setEntryEditing(comm);
              }
            };
            const stop = (fn: () => void) => (e: React.MouseEvent) => {
              e.stopPropagation();
              fn();
            };

            return (
              <li key={comm.id}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleRowClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRowClick();
                    }
                  }}
                  className="px-6 py-3 hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none focus:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-xs text-gray-500">
                          {new Date(comm.occurred_at).toLocaleDateString('de-DE')}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${KIND_BADGES[comm.kind] || 'bg-gray-100 text-gray-800'}`}>
                          {KIND_LABELS[comm.kind] || comm.kind}
                        </span>
                        {statusBadge && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${statusBadge.class}`}>
                            {statusBadge.label}
                          </span>
                        )}
                        {directionPill && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${directionPill.class}`}>
                            {directionPill.label}
                          </span>
                        )}
                        {comm.contact_name && (
                          <span className="text-gray-700 truncate">
                            {comm.direction === 'outgoing' ? 'An ' : 'Von '}
                            <span className="font-medium">{comm.contact_name}</span>
                          </span>
                        )}
                      </div>
                      {comm.subject && (
                        <p className="text-sm text-gray-900 mt-0.5 truncate">{comm.subject}</p>
                      )}
                      {comm.document_filename && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          Anhang: <span className="text-blue-600">{comm.document_filename}</span>
                          {comm.template_name && (
                            <span className="text-gray-400"> · Vorlage: {comm.template_name}</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-3 flex-shrink-0 items-center text-xs">
                      <button
                        onClick={stop(() => editCommunication(comm))}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        Bearbeiten
                      </button>
                      {(isLetter || isEmail) && isOutgoing && isDraft && (
                        <button
                          onClick={stop(() => {
                            const msg = isLetter && comm.template_name
                              ? 'Brief jetzt versenden? Es wird ein PDF erzeugt; der Entwurf ist danach nicht mehr bearbeitbar.'
                              : 'Eintrag als versendet markieren?';
                            if (confirm(msg)) finalizeMut.mutate(comm.id);
                          })}
                          disabled={finalizeMut.isPending}
                          className="text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          Versenden
                        </button>
                      )}
                      {isLetter && isDraft && comm.document_id && comm.document_filename && (
                        <button
                          onClick={stop(() => documentsApi.downloadDocument(comm.document_id as number, comm.document_filename as string))}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          ODT
                        </button>
                      )}
                      {isLetter && isSent && comm.document_id && comm.document_filename && isPdfDoc && (
                        <button
                          onClick={stop(() => documentsApi.downloadDocument(comm.document_id as number, comm.document_filename as string))}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          PDF
                        </button>
                      )}
                      {/* Generic download for non-letter attachments + non-PDF letter docs we don't already cover */}
                      {!isLetter && comm.document_id && comm.document_filename && (
                        <button
                          onClick={stop(() => documentsApi.downloadDocument(comm.document_id as number, comm.document_filename as string))}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {isOdtDoc ? 'ODT' : isPdfDoc ? 'PDF' : 'Anhang'}
                        </button>
                      )}
                      <button
                        onClick={stop(() => {
                          if (confirm('Eintrag wirklich löschen? Das angehängte Dokument bleibt erhalten.')) {
                            deleteMut.mutate(comm.id);
                          }
                        })}
                        className="text-red-500 hover:text-red-700"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {letterWizardOpen && (
        <LetterWizardModal
          entityType={entityType}
          entityId={entityId}
          onClose={() => setLetterWizardOpen(false)}
          onCreated={(comm) => {
            const doc = syntheticDocFromComm(comm);
            // Don't auto-open ODT in the viewer (it isn't a previewable PDF/image).
            if (doc && doc.mime_type === 'application/pdf') setViewingDoc(doc);
          }}
        />
      )}

      {letterEditing && (
        <LetterWizardModal
          entityType={entityType}
          entityId={entityId}
          existing={letterEditing}
          onClose={() => setLetterEditing(null)}
        />
      )}

      {entryModalOpen && (
        <CommunicationEntryModal
          entityType={entityType}
          entityId={entityId}
          onClose={() => setEntryModalOpen(false)}
        />
      )}

      {entryEditing && (
        <CommunicationEntryModal
          entityType={entityType}
          entityId={entityId}
          existing={entryEditing}
          onClose={() => setEntryEditing(null)}
        />
      )}

      {viewingDoc && (
        <DocumentViewer
          document={viewingDoc}
          onClose={() => setViewingDoc(null)}
        />
      )}
    </div>
  );
}
